import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/TrueFalse.scss";

const TrueFalse = ({
  questions = [],
  current: propCurrent,
  setCurrent: propSetCurrent,
  answers: propAnswers,
  setAnswers: propSetAnswers,
  onNext,
  onPrev,
  mode,
  isAdmin,
  questionId,
}) => {
  const [searchParams] = useSearchParams();
  const isReviewParam = searchParams.get("review") === "true";

  const parsedQuestions = useMemo(() => {
    let source = Array.isArray(questions)
      ? questions
      : [questions].filter(Boolean);

    if (source.length > 0 && typeof source[0] === "string") {
      try {
        source = source.map((q) => (typeof q === "string" ? JSON.parse(q) : q));
      } catch (e) {
        console.error("Error parsing questions:", e);
        return source;
      }
    }

    return source.map((q) => {
      if (
        q &&
        q.options &&
        !Array.isArray(q.options) &&
        typeof q.options === "object"
      ) {
        const opts = q.options;
        const normalizedOptions = Object.keys(opts).map((key) => ({
          [key]: opts[key],
        }));
        return { ...q, options: normalizedOptions };
      }
      return q;
    });
  }, [questions]);

  const [internalCurrent, setInternalCurrent] = useState(0);
  const [internalAnswers, setInternalAnswers] = useState({});
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const AUTO_DELAY_MS = 1800;

  const isCurrentControlled = propCurrent !== undefined;
  const isAnswersControlled = propAnswers !== undefined;

  const current = isCurrentControlled ? propCurrent : internalCurrent;
  const answers = isAnswersControlled ? propAnswers : internalAnswers;

  const setCurrent = (val) => {
    if (isCurrentControlled && typeof propSetCurrent === "function") {
      propSetCurrent(val);
    } else {
      setInternalCurrent(val);
    }
  };

  const setAnswers = (val) => {
    if (isAnswersControlled && typeof propSetAnswers === "function") {
      propSetAnswers(val);
    } else {
      setInternalAnswers(val);
    }
  };

  const currentQ = parsedQuestions[current];

  const clearTimers = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startAutoAdvance = () => {
    clearTimers();

    timeoutRef.current = setTimeout(() => {
      clearTimers();
      if (current < parsedQuestions.length - 1) {
        setCurrent((i) => i + 1);
      } else if (onNext) {
        onNext();
      }
    }, AUTO_DELAY_MS);
  };

  const handleSelect = (value) => {
    if (!currentQ) return;
    const finalQuestionId = questionId || currentQ.question_id || currentQ.question;
    const selected = answers[finalQuestionId];
    const answered = typeof selected === "string";
    const showFeedback = mode === "review" || isAdmin;

    if (showFeedback) return;

    setAnswers((prev) => ({ ...prev, [finalQuestionId]: value }));
    if (current < parsedQuestions.length - 1) startAutoAdvance();
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    clearTimers();
  }, [current, questions]);

  if (!parsedQuestions || parsedQuestions.length === 0) {
    return <div className="tf-empty">No questions provided.</div>;
  }

  const finalQuestionId = questionId || currentQ?.question_id || currentQ?.question;
  const selected = finalQuestionId ? answers[finalQuestionId] : undefined;
  const answered = finalQuestionId && typeof selected === "string";

  const correctAnswer =
    currentQ.answer === true || currentQ.answer === "A" ? "A" : "B";

  return (
    <div className="tf-container" key={`tf-${current}`}>
      <div>
        <div className="tf-card" key={`tf-card-${current}`}>
          <div className="tf-question" aria-live="polite">
            {currentQ.question}
          </div>

          <div className="tf-options" role="group" aria-label="True or False">
            {currentQ.options && currentQ.options.length > 0
              ? currentQ.options.map((option, index) => {
                  const optionKey = Object.keys(option)[0];
                  const optionValue = option[optionKey];
                  const showFeedback = mode === "review" || isAdmin;
                  const normalizedSelected = selected !== undefined && selected !== null ? String(selected).toUpperCase() : "";
                  const isSelected = answered && (
                    normalizedSelected === String(optionKey).toUpperCase() ||
                    normalizedSelected === String(optionValue).toUpperCase() ||
                    (optionKey === "A" && (normalizedSelected === "TRUE" || normalizedSelected === "YES")) ||
                    (optionKey === "B" && (normalizedSelected === "FALSE" || normalizedSelected === "NO"))
                  );
                  const isCorrect = showFeedback && optionKey === correctAnswer;
                  const isIncorrect =
                    showFeedback && isSelected && optionKey !== correctAnswer;

                  return (
                    <button
                      key={optionKey}
                      className={[
                        "tf-option",
                        isSelected ? "selected" : "",
                        isCorrect ? "correct" : "",
                        isIncorrect ? "incorrect" : "",
                      ].join(" ")}
                      onClick={() => handleSelect(optionKey)}
                      disabled={showFeedback}
                      aria-pressed={isSelected}
                    >
                      <span className="tf-option-key">{optionKey}</span>
                      <span className="tf-option-label">{optionValue}</span>
                    </button>
                  );
                })
              : [true, false].map((val) => {
                  const optionKey = val ? "A" : "B";
                  const label = val ? "True" : "False";
                  const showFeedback = mode === "review" || isAdmin;
                  const normalizedSelected = selected !== undefined && selected !== null ? String(selected).toUpperCase() : "";
                  const isSelected = answered && (
                    normalizedSelected === String(optionKey).toUpperCase() ||
                    normalizedSelected === String(label).toUpperCase() ||
                    (optionKey === "A" && (normalizedSelected === "TRUE" || normalizedSelected === "YES")) ||
                    (optionKey === "B" && (normalizedSelected === "FALSE" || normalizedSelected === "NO"))
                  );
                  const isCorrect =
                    showFeedback && val === (correctAnswer === "A");
                  const isIncorrect =
                    showFeedback &&
                    isSelected &&
                    val !== (correctAnswer === "A");

                  return (
                    <button
                      key={String(val)}
                      className={[
                        "tf-option",
                        isSelected ? "selected" : "",
                        isCorrect ? "correct" : "",
                        isIncorrect ? "incorrect" : "",
                      ].join(" ")}
                      onClick={() => handleSelect(optionKey)}
                      disabled={showFeedback}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="tf-option-key"
                        style={{
                          borderWidth: 2,
                          borderColor: isIncorrect
                            ? "var(--incorrect-red)"
                            : isSelected && isCorrect
                              ? "var(--color-primary)"
                              : isSelected
                                ? "var(--selected-blue-color)"
                                : isCorrect
                                  ? "var(--color-primary)"
                                  : "",
                        }}
                      >
                        {optionKey}
                      </span>
                      <span className="tf-option-label">{label}</span>
                    </button>
                  );
                })}
          </div>

          {(mode === "review" || isAdmin) &&
            currentQ.explanation &&
            (isReviewParam ||
              mode === "review" ||
              isAdmin) && (
              <div
                className={`explanation-box ${
                  selected === correctAnswer ? "correct" : "incorrect"
                }`}
              >
                <div className="explanation-text">
                  <strong>Explanation:</strong> {currentQ.explanation}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default TrueFalse;
